"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProfileEntitlementEnabled = exports.useActiveOrganization = exports.useActiveOrganizationId = exports.useGetSessionContext = exports.useSessionContext = exports.useIsStudio = exports.useStudioMetadata = exports.useProfile = void 0;
const react_1 = __importDefault(require("react"));
const withUserContext_1 = require("../contexts/withUserContext");
/**
 * Will return the profile for the active user, if any. If no user is logged in, it will return `null`.
 *
 * @example
 *
 * ```tsx
 * import { useProfile } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const profile = useProfile();
 *
 *   ...
 * };
 * ```
 *
 * @returns The profile
 */
function useProfile() {
    const userContext = react_1.default.useContext(withUserContext_1.UserContext);
    return userContext.profile;
}
exports.useProfile = useProfile;
/**
 * When navigating inside Anypoint Studio, will return detailed session data for Anypoint Studio.
 *
 * @example
 *
 * ```tsx
 * import { useStudioMetadata } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const studioMetadata = useStudioMetadata();
 *
 *   ...
 * };
 * ```
 *
 * @returns Metadata for Anypoint Studio
 */
function useStudioMetadata() {
    const userContext = react_1.default.useContext(withUserContext_1.UserContext);
    return userContext.studioMetadata;
}
exports.useStudioMetadata = useStudioMetadata;
/**
 * Will return `true` if Exchange is navigating inside Anypoint Studio.
 *
 * @example
 *
 * ```tsx
 * import { useIsStudio } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const isStudio = useIsStudio();
 *
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the user is opening Exchange from Anypoint Studio
 */
function useIsStudio() {
    const userContext = react_1.default.useContext(withUserContext_1.UserContext);
    return userContext.isStudio;
}
exports.useIsStudio = useIsStudio;
/**
 * Will return the session context for the active user. Context contains both authorization and IP information for the user. If no user is logged in, it will return an object with the IP but not the authorization token.
 *
 * @example
 *
 * ```ts
 * import fetchFactory from '@mulesoft/anypoint-fetch';
 * import { useSessionContext } from '@mulesoft/exchange-react-hooks';
 *
 * const fetch = fetchFactory();
 *
 * const MyComponent = ({ onData }) => {
 *   const context = useSessionContext();
 *
 *   async function getData() {
 *     try {
 *       const response = await fetch('http://my-site/my-page', { context });
 *       const result = await response.json();
 *
 *       onData(result);
 *     } catch (error) {
 *       handleError(error);
 *     }
 *   }
 *
 *   React.useEffect(() => {
 *     getData();
 *   }, []);
 *
 *   ...
 * };
 * ```
 *
 * @returns The context of the user including the authorization token
 */
function useSessionContext() {
    const userContext = react_1.default.useContext(withUserContext_1.UserContext);
    return userContext.context;
}
exports.useSessionContext = useSessionContext;
/**
 * Will return a function that returns the session context for the active user. This is useful when running a function with the context inside a memoized callback.
 *
 * @example
 *
 * ```ts
 * import fetchFactory from '@mulesoft/anypoint-fetch';
 * import { useGetSessionContext } from '@mulesoft/exchange-react-hooks';
 *
 * const fetch = fetchFactory();
 *
 * const MyComponent = ({ onData }) => {
 *   const getContext = useGetSessionContext();
 *
 *   async function getData() {
 *     try {
 *       const response = await fetch('http://my-site/my-page', { context: getContext() });
 *       const result = await response.json();
 *
 *       onData(result);
 *     } catch (error) {
 *       handleError(error);
 *     }
 *   }
 *
 *   React.useEffect(() => {
 *     getData();
 *   }, []);
 *
 *   ...
 * };
 * ```
 *
 * @returns The context of the user including the authorization token
 */
function useGetSessionContext() {
    const userContext = react_1.default.useContext(withUserContext_1.UserContext);
    return userContext.getContext;
}
exports.useGetSessionContext = useGetSessionContext;
/**
 * Return the organizationId for the selected organization in the navigation bar.
 *
 * @example
 *
 * ```tsx
 * import { useActiveOrganizationId } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const activeOrganizationId = useActiveOrganizationId();
 *
 *   ...
 * };
 * ```
 *
 * @returns The active organization Id for the user
 */
function useActiveOrganizationId() {
    const profile = useProfile();
    if (profile) {
        return profile.properties && profile.properties.cs_auth
            ? profile.properties.cs_auth.activeOrganizationId
            : profile.organization.id;
    }
    return null;
}
exports.useActiveOrganizationId = useActiveOrganizationId;
/**
 * Return the selected organization in the navigation bar.
 *
 * @example
 *
 * ```tsx
 * import { useActiveOrganization } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const activeOrganization = useActiveOrganization();
 *
 *   ...
 * };
 * ```
 *
 * @returns The active organization for the user
 */
function useActiveOrganization() {
    const profile = useProfile();
    const activeOrganizationId = useActiveOrganizationId();
    if (profile && profile.contributorOfOrganizations) {
        return profile.contributorOfOrganizations.find((organization) => organization.id === activeOrganizationId);
    }
    return null;
}
exports.useActiveOrganization = useActiveOrganization;
/**
 * Returns true if an entitlement or a sub-entitlement is enabled for the user profile organization.
 *
 * @example
 *
 * ```tsx
 * import { useProfileEntitlementEnabled } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const isEntitlementEnabled = useProfileEntitlementEnabled("entitlement-key");
 *
 *   ...
 * };
 * ```
 * @param entitlementKey Entitlement parent key
 * @param subEntitlementKey Optional sub-entitlement key inside the entitlement parent
 * @returns The if an entitlement is enabled
 */
function useProfileEntitlementEnabled(entitlementKey, subEntitlementKey) {
    var _a, _b;
    const profile = useProfile();
    const entitlements = (_a = profile === null || profile === void 0 ? void 0 : profile.organization) === null || _a === void 0 ? void 0 : _a.entitlements;
    if (!entitlements) {
        return false;
    }
    const entitlement = entitlements[entitlementKey];
    if (!entitlement) {
        return false;
    }
    if (subEntitlementKey) {
        const subEntitlement = entitlement[subEntitlementKey];
        if (typeof subEntitlement === 'boolean') {
            return subEntitlement;
        }
        return (_b = subEntitlement === null || subEntitlement === void 0 ? void 0 : subEntitlement.enabled) !== null && _b !== void 0 ? _b : false;
    }
    if (typeof entitlement === 'boolean') {
        return entitlement;
    }
    if ('enabled' in entitlement && typeof entitlement.enabled === 'boolean') {
        return entitlement.enabled;
    }
    return false;
}
exports.useProfileEntitlementEnabled = useProfileEntitlementEnabled;
